import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Settings2, Award, Target } from "lucide-react";
import api from "../lib/api.js";

export default function AnnualGoalCard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/annual-goal").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const pct = Math.min(100, data.percent_complete);
  const remaining = data.remaining_steps.toLocaleString();
  const earned = data.milestones.filter((m) => m.achieved);

  return (
    <div className="ps-card p-5 relative overflow-hidden" data-testid="annual-goal-card">
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#FBBF24]/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-[#4ade80]/10 blur-3xl pointer-events-none" />

      <div className="relative flex items-start justify-between">
        <div>
          <div className="ps-chip ps-chip-amber">
            <Target size={11} /> {data.year} annual goal
          </div>
          <div className="ps-heading text-2xl font-extrabold mt-3 ps-tabular">
            {data.target_steps.toLocaleString()}
            <span className="text-base text-slate-500 ml-1.5 ps-serif ps-italic font-normal not-italic ml-2">steps</span>
          </div>
        </div>
        <Link
          to="/annual-goal"
          data-testid="annual-goal-edit"
          className="w-9 h-9 rounded-full bg-[#15303E] border border-[#1B364A] flex items-center justify-center text-slate-300 hover:text-white hover:border-[#4ade80]"
          aria-label="Adjust annual goal"
        >
          <Settings2 size={15} />
        </Link>
      </div>

      <div className="relative mt-5 flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Remaining</div>
          <div className="ps-heading text-4xl font-extrabold ps-tabular" data-testid="annual-goal-remaining">
            {remaining}
          </div>
          <div className="text-[11px] text-slate-500 ps-serif ps-italic">steps to go</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold">Progress</div>
          <div className="ps-heading text-4xl font-extrabold ps-gradient-text ps-tabular" data-testid="annual-goal-pct">
            {pct.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="relative mt-5 h-3 rounded-full bg-[#0E1F2A] overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#FBBF24] via-[#4ade80] to-[#22d3ee] transition-all duration-1000 relative"
          style={{ width: `${pct}%` }}
        >
          <div className="absolute inset-0 ps-shimmer" />
        </div>
      </div>

      <div className="relative mt-5 flex items-center justify-between">
        <div className="flex -space-x-2">
          {earned.length === 0 ? (
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5 ps-serif ps-italic">
              <Sparkles size={11} /> No milestone badges yet — keep walking.
            </div>
          ) : (
            earned.slice(0, 5).map((m) => (
              <div
                key={m.code}
                title={m.title}
                className="w-8 h-8 rounded-full border-2 border-[#0E1F2A] flex items-center justify-center text-[10px] font-extrabold ps-tabular"
                style={{ background: m.color, color: "#050E15", boxShadow: `0 0 16px ${m.color}66` }}
              >
                {m.pct >= 100 ? "★" : m.pct}
              </div>
            ))
          )}
        </div>
        <Link
          to="/badges"
          data-testid="annual-goal-badges-link"
          className="text-[11px] font-bold inline-flex items-center gap-1 ps-gradient-text hover:opacity-80"
        >
          <Award size={12} className="text-[#FBBF24]" /> {earned.length}/{data.milestones.length} badges
        </Link>
      </div>
    </div>
  );
}
